/**
 * Treaty Room - Collaborative Negotiation Portal for NEGOTIATOR
 * Split-pane interface with chat and shared draft board
 */

import React, { useState, useEffect, useRef } from 'react';

const TreatyRoom = ({ 
  socket, 
  gameId, 
  playerId, 
  players, 
  isOpen, 
  onClose, 
  negotiationPartner,
  gameState 
}) => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Draft Board state
  const [draftOrders, setDraftOrders] = useState({});
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hoveredSector, setHoveredSector] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState('negotiate'); // 'negotiate' or 'orders'
  const [isDragging, setIsDragging] = useState(false);

  // Socket event handlers
  useEffect(() => {
    if (!socket || !isOpen) return;

    // Chat events
    socket.on('negotiationMessage', (data) => {
      if (data.gameId === gameId) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          playerId: data.playerId,
          playerName: data.playerName,
          message: data.message,
          timestamp: new Date()
        }]);
      }
    });

    // Draft Board events
    socket.on('draftOrderProposed', (data) => {
      if (data.gameId === gameId) {
        setDraftOrders(prev => ({
          ...prev,
          [data.orderId]: {
            ...data.order,
            status: 'PROPOSED',
            proposedBy: data.playerId
          }
        }));
      }
    });

    socket.on('draftOrderRatified', (data) => {
      if (data.gameId === gameId) {
        setDraftOrders(prev => ({
          ...prev,
          [data.orderId]: {
            ...prev[data.orderId],
            status: 'RATIFIED',
            ratifiedBy: [...(prev[data.orderId]?.ratifiedBy || []), data.playerId]
          }
        }));
      }
    });

    socket.on('draftOrderRemoved', (data) => {
      if (data.gameId === gameId) {
        setDraftOrders(prev => {
          const newOrders = { ...prev };
          delete newOrders[data.orderId];
          return newOrders;
        });
      }
    });

    return () => {
      socket.off('negotiationMessage');
      socket.off('draftOrderProposed');
      socket.off('draftOrderRatified');
      socket.off('draftOrderRemoved');
    };
  }, [socket, gameId, isOpen]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send chat message
  const sendMessage = () => {
    if (newMessage.trim() && socket) {
      socket.emit('sendNegotiationMessage', {
        gameId,
        playerId,
        message: newMessage.trim(),
        partnerId: negotiationPartner?.id
      });
      setNewMessage('');
    }
  };

  // Handle unit selection and dragging
  const handleUnitSelect = (unit) => {
    setSelectedUnit(unit);
  };

  const handleSectorDrop = (sectorId) => {
    if (selectedUnit && socket) {
      const orderId = `${selectedUnit.id}-${sectorId}-${Date.now()}`;
      const draftOrder = {
        orderId,
        unitId: selectedUnit.id,
        unitType: selectedUnit.type,
        action: 'MOVE',
        fromSector: selectedUnit.currentSector,
        toSector: sectorId,
        playerId: playerId
      };

      socket.emit('proposeDraftOrder', {
        gameId,
        orderId,
        order: draftOrder,
        partnerId: negotiationPartner?.id
      });

      setSelectedUnit(null);
    }
  };

  // Ratify a draft order
  const ratifyOrder = (orderId) => {
    if (socket) {
      socket.emit('ratifyDraftOrder', {
        gameId,
        orderId,
        playerId,
        partnerId: negotiationPartner?.id
      });
    }
  };

  // Remove a draft order
  const removeOrder = (orderId) => {
    if (socket) {
      socket.emit('removeDraftOrder', {
        gameId,
        orderId,
        partnerId: negotiationPartner?.id
      });
    }
  };

  // Send ratified orders to official orders
  const sendToOfficial = () => {
    const ratifiedOrders = Object.values(draftOrders)
      .filter(order => order.status === 'RATIFIED')
      .filter(order => order.playerId === playerId);

    if (ratifiedOrders.length > 0 && socket) {
      socket.emit('commitNegotiatedOrders', {
        gameId,
        orders: ratifiedOrders,
        playerId
      });
      
      // Clear ratified orders from draft
      setDraftOrders(prev => {
        const newOrders = { ...prev };
        ratifiedOrders.forEach(order => {
          delete newOrders[order.orderId];
        });
        return newOrders;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '10%',
      left: '10%',
      right: '10%',
      bottom: '10%',
      backgroundColor: 'white',
      border: '2px solid #2196F3',
      borderRadius: '8px',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
    }}>
      {/* Header */}
      <div style={{
        padding: '15px 20px',
        borderBottom: '1px solid #ddd',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ margin: 0, color: '#2196F3' }}>
            🤝 Treaty Room
          </h3>
          <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
            Negotiating with {negotiationPartner?.name || 'Partner'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setActiveTab('negotiate')}
            style={{
              padding: '8px 16px',
              border: activeTab === 'negotiate' ? '2px solid #2196F3' : '1px solid #ddd',
              backgroundColor: activeTab === 'negotiate' ? '#e3f2fd' : 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📝 Negotiate
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              padding: '8px 16px',
              border: activeTab === 'orders' ? '2px solid #2196F3' : '1px solid #ddd',
              backgroundColor: activeTab === 'orders' ? '#e3f2fd' : 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ⚡ Orders
          </button>
          <button onClick={onClose} style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {activeTab === 'negotiate' && (
          <>
            {/* Left Pane: Chat */}
            <div style={{
              flex: 1,
              borderRight: '1px solid #ddd',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
              }}>
                <h4 style={{ margin: 0 }}>💬 Communications</h4>
              </div>
              
              {/* Messages */}
              <div style={{
                flex: 1,
                padding: '10px',
                overflowY: 'auto',
                maxHeight: 'calc(100% - 120px)'
              }}>
                {messages.map(msg => (
                  <div key={msg.id} style={{
                    marginBottom: '10px',
                    padding: '8px',
                    backgroundColor: msg.playerId === playerId ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${msg.playerId === playerId ? '#2196F3' : '#ff9800'}`
                  }}>
                    <div style={{
                      fontSize: '0.8em',
                      color: '#666',
                      marginBottom: '2px'
                    }}>
                      {msg.playerName} • {msg.timestamp.toLocaleTimeString()}
                    </div>
                    <div>{msg.message}</div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div style={{
                padding: '10px',
                borderTop: '1px solid #eee',
                display: 'flex',
                gap: '8px'
              }}>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <button
                  onClick={sendMessage}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Send
                </button>
              </div>
            </div>

            {/* Right Pane: Draft Board */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
              }}>
                <h4 style={{ margin: 0 }}>📋 Draft Board</h4>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.8em', color: '#666' }}>
                  Collaborative order planning
                </p>
              </div>

              {/* Draft Orders */}
              <div style={{
                flex: 1,
                padding: '10px',
                overflowY: 'auto'
              }}>
                {Object.values(draftOrders).map(order => (
                  <div key={order.orderId} style={{
                    marginBottom: '8px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: order.status === 'RATIFIED' ? '#e8f5e8' : '#fff3cd'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {order.unitType} → {order.toSector}
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          Proposed by: {players.find(p => p.id === order.proposedBy)?.name}
                        </div>
                        <div style={{
                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.7em',
                            backgroundColor: order.status === 'RATIFIED' ? '#4caf50' : '#ff9800',
                            color: 'white'
                          }}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {order.status === 'PROPOSED' && (
                          <button
                            onClick={() => ratifyOrder(order.orderId)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.8em',
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Ratify
                          </button>
                        )}
                        <button
                          onClick={() => removeOrder(order.orderId)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.8em',
                            cursor: 'pointer'
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {Object.keys(draftOrders).length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontStyle: 'italic',
                    marginTop: '40px'
                  }}>
                    No draft orders yet. Start by selecting your units and proposing moves.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'orders' && (
          <div style={{ width: '100%', display: 'flex' }}>
            {/* Left: Agreed Orders */}
            <div style={{
              flex: 1,
              borderRight: '1px solid #ddd',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#e8f5e8'
              }}>
                <h4 style={{ margin: 0, color: '#2e7d32' }}>✅ Agreed Orders</h4>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.8em', color: '#666' }}>
                  Ready to send to official
                </p>
              </div>
              <div style={{ flex: 1, padding: '10px', overflowY: 'auto' }}>
                {Object.values(draftOrders)
                  .filter(order => order.status === 'RATIFIED' && order.playerId === playerId)
                  .map(order => (
                    <div key={order.orderId} style={{
                      marginBottom: '8px',
                      padding: '10px',
                      backgroundColor: '#e8f5e8',
                      border: '1px solid #4caf50',
                      borderRadius: '4px'
                    }}>
                      <strong>{order.unitType} → {order.toSector}</strong>
                    </div>
                  ))
                }
                <button
                  onClick={sendToOfficial}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '10px'
                  }}
                >
                  🚀 Send to Official Orders
                </button>
              </div>
            </div>

            {/* Right: Official Orders */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#fff3e0'
              }}>
                <h4 style={{ margin: 0, color: '#f57c00' }}>⚡ Official Orders</h4>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.8em', color: '#666' }}>
                  Will be executed at deadline
                </p>
              </div>
              <div style={{ flex: 1, padding: '10px', overflowY: 'auto' }}>
                {/* This would show the player's actual submitted orders */}
                <div style={{
                  textAlign: 'center',
                  color: '#666',
                  fontStyle: 'italic',
                  marginTop: '40px'
                }}>
                  Official orders will appear here once submitted
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreatyRoom;